import { v4 as uuid } from 'uuid';

const SESSION_STORAGE_KEY = 'my-session';

const isBrowser = typeof window !== 'undefined';

/** The header the Carvana backends read to stitch a browser session to their server logs. */
export const CORRELATION_ID_HEADER = 'x-correlation-id';

/**
 * Memoized for the document lifetime because the storage paths below can hand back a fresh
 * uuid on every call: private browsing and locked-down webviews make sessionStorage throw,
 * and the non-browser branch has nothing to persist to. Without this cache those cases would
 * stamp a different correlation id on every HTTP request, which is precisely the state that
 * makes a trace unjoinable.
 */
let cachedSessionId: string | undefined;

export const getOrCreateSessionId = (): string => {
  if (cachedSessionId) return cachedSessionId;

  if (!isBrowser) {
    cachedSessionId = uuid();
    return cachedSessionId;
  }

  try {
    const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedSessionId) {
      cachedSessionId = storedSessionId;
      return cachedSessionId;
    }

    // Cached before the write so a throwing setItem, as under a full storage quota, keeps the
    // id we just minted instead of falling through and minting a second one.
    cachedSessionId = uuid();
    sessionStorage.setItem(SESSION_STORAGE_KEY, cachedSessionId);
  } catch {
    if (!cachedSessionId) {
      cachedSessionId = uuid();
    }
  }

  return cachedSessionId;
};

/**
 * Retires the current id and installs a replacement, in memory and in storage.
 *
 * Overwrites rather than removes, and mints before touching storage, so the rotation holds even
 * when storage refuses the write. Removing would leave the old id in place on any browser whose
 * removeItem throws while reads still succeed, which is the one outcome this must never produce.
 */
export const rotateSessionId = (): void => {
  cachedSessionId = uuid();

  if (!isBrowser) return;

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, cachedSessionId);
  } catch {
    // Storage is unavailable, so the in-memory rotation above is the whole reset.
  }
};

/** Test-only. Drops the cache without touching storage so a suite can seed a known id. */
export const resetSessionIdCache = (): void => {
  cachedSessionId = undefined;
};
